import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Wheat, Building2, Globe, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Login = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t, languages } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingWarehouses, setFetchingWarehouses] = useState(false);
  const [lastUsedWarehouse, setLastUsedWarehouse] = useState(null);

  // Load last used warehouse from localStorage on mount
  useEffect(() => {
    const savedWarehouse = localStorage.getItem('lastUsedWarehouse');
    if (savedWarehouse) {
      setLastUsedWarehouse(savedWarehouse);
    }
  }, []);

  // Fetch warehouses when email is entered
  const fetchUserWarehouses = async (userEmail) => {
    if (!userEmail) return;
    
    setFetchingWarehouses(true);
    try {
      const response = await axios.get(`${API}/auth/user-warehouses/${encodeURIComponent(userEmail)}`);
      setWarehouses(response.data);
      
      // Check if last used warehouse is in the list
      const savedWarehouse = localStorage.getItem('lastUsedWarehouse');
      if (savedWarehouse && response.data.includes(savedWarehouse)) {
        setWarehouse(savedWarehouse);
      } else if (response.data.length > 0) {
        // Default to first warehouse if saved one is not available
        setWarehouse(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      setWarehouses(['Main Warehouse']);
      setWarehouse('Main Warehouse');
    } finally {
      setFetchingWarehouses(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!warehouse) {
      toast.error(t('login.warehouse') + " - Required");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/auth/login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}&warehouse=${encodeURIComponent(warehouse)}`,
        {},
        { withCredentials: true }
      );
      
      // Save last used warehouse to localStorage
      localStorage.setItem('lastUsedWarehouse', warehouse);
      
      toast.success("Login successful!");
      navigate("/dashboard", { state: { user: response.data.user, warehouse: response.data.warehouse } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-40 bg-white">
            <Globe className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                {lang.nativeName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src="/logo.jpeg" alt="SPS Groups Logo" className="h-24 w-auto" />
          </div>
          <CardTitle className="text-3xl font-outfit font-bold text-stone-900">
            SPS GROUPS
          </CardTitle>
          <CardDescription className="text-base mt-2">Inventory Management System</CardDescription>
          <CardDescription className="text-sm mt-1">{t('login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value) {
                    fetchUserWarehouses(e.target.value);
                  }
                }}
                onBlur={() => email && fetchUserWarehouses(email)}
                placeholder={t('login.email')}
                required
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.password')}
                required
                data-testid="login-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="warehouse">{t('login.warehouse')} *</Label>
              <Select 
                value={warehouse} 
                onValueChange={setWarehouse}
                disabled={fetchingWarehouses || warehouses.length === 0}
              >
                <SelectTrigger data-testid="warehouse-select" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-stone-500" />
                  <SelectValue placeholder={fetchingWarehouses ? t('common.loading') : t('login.warehouse')} />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh} value={wh}>
                      <span className="flex items-center gap-2">
                        {wh}
                        {lastUsedWarehouse === wh && (
                          <Star className="w-3 h-3 text-amber-500 fill-amber-500" title="Last used" />
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lastUsedWarehouse && warehouses.includes(lastUsedWarehouse) && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Star className="w-3 h-3" /> Last used: {lastUsedWarehouse}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full bg-lime-700 text-white hover:bg-lime-800"
              disabled={loading || !warehouse}
              data-testid="login-submit"
            >
              {loading ? t('common.loading') : t('login.signIn')}
            </Button>
          </form>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-stone-500">Or continue with</span>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            data-testid="google-login"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;